import {
  useEffect,
  useRef,
  useCallback
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { Geolocation } from '@capacitor/geolocation'
import { setXmppLiveLocation } from 'store/chat/slice'
import { usersSelectors } from 'store'
import { LOCATION_TYPE } from 'utils/constants'

const COORD_DELTA = 0.0001

export const useGeoLocation = () => {
  const watcherIdRef = useRef(null)
  const prevCoordRef = useRef({ lat: Number.NaN, lng: Number.NaN })
  const coordThresholdRef = useRef(Number.NaN)

  const dispatch = useDispatch()
  const { activeProfile } = useSelector(usersSelectors)

  const onError = useCallback((error) => {
    console.error('Geolocation error:', error)
  }, [])

  const onSuccess = useCallback((loc) => {
    if (
      sessionStorage.getItem('id') === null
      && (
        localStorage.getItem('isNotifOpen') === null
        || localStorage.getItem('isNotifOpen') === 'false'
      )
    ) {
      const accuracy = loc.coords.accuracy ?? 0
      coordThresholdRef.current = accuracy + COORD_DELTA

      const { lat, lng } = prevCoordRef.current
      const isFirstTime = Number.isNaN(lat) || Number.isNaN(lng)
      const isRegularUpdate = Math.abs(loc.coords.latitude - lat) > coordThresholdRef.current
        || Math.abs(loc.coords.longitude - lng) > coordThresholdRef.current

      if (isFirstTime || isRegularUpdate) {
        prevCoordRef.current = {
          lat: loc.coords.latitude,
          lng: loc.coords.longitude
        }
        dispatch(setXmppLiveLocation(loc.coords))
        const event = new CustomEvent('custom-chat-update_gps_coordinates')
        window.dispatchEvent(event)
      }
    }
  }, [dispatch])

  const clearWatcher = useCallback(async () => {
    if (watcherIdRef.current === null) return
    await Geolocation.clearWatch({ id: watcherIdRef.current })
    watcherIdRef.current = null
    prevCoordRef.current = { lat: Number.NaN, lng: Number.NaN }
    coordThresholdRef.current = Number.NaN
  }, [])

  const startWatcher = useCallback(async () => {
    try {
      const permission = await Geolocation.requestPermissions()
      if (
        permission.location !== 'granted'
        && permission.coarseLocation !== 'granted'
      ) {
        console.warn('Location permission denied')
        return
      }

      const id = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 10000 },
        (loc, err) => {
          if (err) {
            onError(err)
            return
          }
          if (loc) onSuccess(loc)
        }
      )
      watcherIdRef.current = id
    } catch (err) {
      console.error('Failed to start geolocation watcher:', err)
    }
  }, [onSuccess, onError])

  useEffect(() => {
    const activeID = activeProfile?.id
    const activeLocType = activeProfile?.location_type

    if (
      activeID
      && (
        activeLocType?.[LOCATION_TYPE.LiveLocation] === 0
        || activeLocType?.[LOCATION_TYPE.LiveApproximate] === 1
      )
    ) {
      clearWatcher().then(startWatcher)
    } else {
      clearWatcher()
    }

    return () => {
      clearWatcher()
    }
  }, [activeProfile?.location_type, activeProfile?.id, clearWatcher, startWatcher])

  return {}
}
